import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Copy, Check, ExternalLink, Code2, Globe, Link2 } from "lucide-react";
import { toast } from "sonner";

export default function IntegrationsModal({ open, onOpenChange, lang, theme, tt }) {
  const dark = theme === "dark";
  const [width, setWidth] = useState("100%");
  const [height, setHeight] = useState("980");
  const [copied, setCopied] = useState(null);

  const origin = window.location.origin;
  const langParam = lang === "en" ? "?lang=en" : "";
  const shareUrl = `${origin}/${langParam}`;
  const embedUrl = `${origin}/embed${langParam}`;
  const iframeSnippet = `<iframe src="${embedUrl}" width="${width}" height="${height}" style="border:0;max-width:100%" title="Prolancer Calculator" loading="lazy"></iframe>`;
  const apiBase = origin;

  const doCopy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success(tt.copied);
      setTimeout(() => setCopied(null), 1600);
    } catch {
      toast.error("Copy failed");
    }
  };

  const box = dark
    ? "bg-[#1f2a47] border-[#2e3a5c] text-white"
    : "bg-white border-[#e5e5ea] text-[#2d2c35]";
  const codeBox = dark
    ? "bg-black/40 border-[#2e3a5c] text-[#18c98f]"
    : "bg-[#f3f8ff] border-[#e5e5ea] text-[#12a777]";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="integrations-modal"
        className={`max-w-2xl max-h-[90vh] overflow-y-auto ${dark ? "bg-[#1f2a47] border-[#2e3a5c] text-white" : "bg-white"}`}
        dir={lang === "ar" ? "rtl" : "ltr"}
      >
        <DialogHeader>
          <DialogTitle className={`text-xl font-bold ${lang === "ar" ? "font-arabic" : "font-outfit"}`}>
            {tt.title}
          </DialogTitle>
          <DialogDescription className={`${dark ? "text-white/70" : "text-[#2d2c35]/70"} ${lang === "ar" ? "font-arabic" : "font-outfit"}`}>
            {tt.subtitle}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="link" className="mt-2">
          <TabsList className={`grid grid-cols-3 ${dark ? "bg-[#1f2a47]" : "bg-[#f3f8ff]"}`}>
            <TabsTrigger value="link" data-testid="tab-link" className="gap-1">
              <Link2 className="h-3.5 w-3.5" />
              {tt.tabLink}
            </TabsTrigger>
            <TabsTrigger value="embed" data-testid="tab-embed" className="gap-1">
              <Globe className="h-3.5 w-3.5" />
              {tt.tabEmbed}
            </TabsTrigger>
            <TabsTrigger value="api" data-testid="tab-api" className="gap-1">
              <Code2 className="h-3.5 w-3.5" />
              {tt.tabApi}
            </TabsTrigger>
          </TabsList>

          {/* Direct link */}
          <TabsContent value="link" className="mt-4 space-y-3">
            <p className={`text-sm ${dark ? "text-white/80" : "text-[#2d2c35]/80"}`}>{tt.linkDesc}</p>
            <div className="flex gap-2">
              <Input value={shareUrl} readOnly className={`font-mono text-xs ${box}`} data-testid="share-url-input" />
              <Button
                data-testid="copy-share-url"
                variant="outline"
                onClick={() => doCopy("link", shareUrl)}
                className={`gap-1 ${dark ? "border-[#2e3a5c] bg-[#1f2a47] text-white hover:bg-[#2e3a5c]" : ""}`}
              >
                {copied === "link" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied === "link" ? tt.copied : tt.copy}
              </Button>
              <Button
                variant="outline"
                onClick={() => window.open(shareUrl, "_blank")}
                className={`gap-1 ${dark ? "border-[#2e3a5c] bg-[#1f2a47] text-white hover:bg-[#2e3a5c]" : ""}`}
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
          </TabsContent>

          {/* Iframe embed */}
          <TabsContent value="embed" className="mt-4 space-y-3">
            <p className={`text-sm ${dark ? "text-white/80" : "text-[#2d2c35]/80"}`}>{tt.embedDesc}</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={`text-xs ${dark ? "text-white/60" : "text-[#2d2c35]/60"}`}>{tt.widthLabel}</label>
                <Input value={width} onChange={(e) => setWidth(e.target.value)} className={box} data-testid="embed-width" />
              </div>
              <div>
                <label className={`text-xs ${dark ? "text-white/60" : "text-[#2d2c35]/60"}`}>{tt.heightLabel} (px)</label>
                <Input value={height} onChange={(e) => setHeight(e.target.value)} className={box} data-testid="embed-height" />
              </div>
            </div>
            <div className={`relative rounded-lg border p-3 ${codeBox}`}>
              <pre className="text-xs whitespace-pre-wrap break-all font-mono" data-testid="embed-snippet">{iframeSnippet}</pre>
              <Button
                data-testid="copy-embed-snippet"
                variant="outline"
                size="sm"
                onClick={() => doCopy("embed", iframeSnippet)}
                className={`absolute top-2 ${lang === "ar" ? "left-2" : "right-2"} gap-1 h-7 ${dark ? "border-[#2e3a5c] bg-[#1f2a47] text-white hover:bg-[#2e3a5c]" : "bg-white"}`}
              >
                {copied === "embed" ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied === "embed" ? tt.copied : tt.copy}
              </Button>
            </div>
            <div className={`rounded-lg border p-3 text-xs ${box}`}>
              <div className="font-bold mb-1">Preview URL:</div>
              <a
                href={embedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#18c98f] hover:underline break-all font-mono"
              >
                {embedUrl}
              </a>
            </div>
          </TabsContent>

          {/* REST API */}
          <TabsContent value="api" className="mt-4 space-y-3">
            <p className={`text-sm ${dark ? "text-white/80" : "text-[#2d2c35]/80"}`}>{tt.apiDesc}</p>

            <div className={`rounded-lg border p-3 text-xs ${box}`}>
              <div className="font-bold mb-2">{tt.endpoints}:</div>
              <ul className="space-y-1 font-mono">
                <li><span className="text-[#18c98f] font-bold">GET</span>  <span dir="ltr">{apiBase}/api/rates</span></li>
                <li><span className="text-[#18c98f] font-bold">POST</span> <span dir="ltr">{apiBase}/api/pricing/calculate</span></li>
                <li><span className="text-[#18c98f] font-bold">POST</span> <span dir="ltr">{apiBase}/api/pricing/ai-suggest</span></li>
                <li><span className="text-[#18c98f] font-bold">POST</span> <span dir="ltr">{apiBase}/api/quotes</span></li>
                <li><span className="text-[#18c98f] font-bold">GET</span>  <span dir="ltr">{apiBase}/api/quotes</span></li>
              </ul>
            </div>

            <div>
              <div className={`text-xs font-bold mb-1 ${dark ? "text-white/70" : "text-[#2d2c35]/70"}`}>{tt.example}:</div>
              <div className={`relative rounded-lg border p-3 ${codeBox}`}>
                <pre className="text-[11px] whitespace-pre-wrap font-mono leading-relaxed" data-testid="api-curl-example" dir="ltr">{`curl -X POST ${apiBase}/api/pricing/calculate \\
  -H "Content-Type: application/json" \\
  -d '{
    "service": "web_dev",
    "experience": "intermediate",
    "complexity": "medium",
    "unit": "hour",
    "quantity": 10,
    "revisions": 2,
    "rush": false
  }'`}</pre>
                <Button
                  data-testid="copy-api-curl"
                  variant="outline"
                  size="sm"
                  onClick={() => doCopy("api", `curl -X POST ${apiBase}/api/pricing/calculate -H "Content-Type: application/json" -d '{"service":"web_dev","experience":"intermediate","complexity":"medium","unit":"hour","quantity":10,"revisions":2,"rush":false}'`)}
                  className={`absolute top-2 ${lang === "ar" ? "left-2" : "right-2"} gap-1 h-7 ${dark ? "border-[#2e3a5c] bg-[#1f2a47] text-white hover:bg-[#2e3a5c]" : "bg-white"}`}
                >
                  {copied === "api" ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied === "api" ? tt.copied : tt.copy}
                </Button>
              </div>
            </div>

            <div className={`rounded-lg border p-3 text-xs ${box}`}>
              <div className="font-bold mb-1">Response schema (POST /api/pricing/calculate):</div>
              <pre className="text-[11px] whitespace-pre-wrap font-mono" dir="ltr">{`{
  "min_price": 750,
  "avg_price": 1500,
  "max_price": 3000,
  "breakdown": [...]
}`}</pre>
            </div>

            <div className={`text-[11px] leading-relaxed rounded-lg p-3 ${dark ? "bg-[#18c98f]/10 border border-[#18c98f]/40 text-white/90" : "bg-[#18c98f]/10 border border-[#12a777]/30 text-[#2d2c35]"}`}>
              {lang === "ar"
                ? "ملاحظة: نقطة النهاية /api/pricing/ai-suggest محدودة بـ 3 طلبات لكل IP كل ٣٠ يومًا لحماية تكلفة الذكاء الاصطناعي."
                : "Note: /api/pricing/ai-suggest is rate-limited to 5 requests per IP per 30 days to protect AI costs."}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
